import React, { useState, useEffect } from 'react';
import { 
  Star, 
  Plus, 
  MoreVertical,
  Pencil,
  Trash2,
  CheckCircle,
  FileText,
  TrendingUp,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: 'bg-gray-50 text-gray-700 border-gray-200',
  submitted: 'bg-blue-50 text-blue-700 border-blue-200',
  acknowledged: 'bg-emerald-50 text-emerald-700 border-emerald-200'
};

const ratingLabels = {
  1: { label: 'Needs Improvement', color: 'text-red-600' },
  2: { label: 'Below Expectations', color: 'text-orange-600' },
  3: { label: 'Meets Expectations', color: 'text-amber-600' },
  4: { label: 'Exceeds Expectations', color: 'text-emerald-600' },
  5: { label: 'Outstanding', color: 'text-blue-600' }
};

const StarRating = ({ rating, onChange, readonly }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => onChange?.(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          className="disabled:cursor-default"
        >
          <Star
            className={`h-5 w-5 transition-colors ${
              star <= (hover || rating)
                ? 'fill-amber-400 text-amber-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
      {rating > 0 && (
        <span className={`ml-2 text-sm font-medium ${ratingLabels[rating]?.color}`}>
          {ratingLabels[rating]?.label}
        </span>
      )}
    </div>
  );
};

const PerformanceReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [formData, setFormData] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchReviews();
    fetchEmployees();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/reviews`);
      setReviews(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/employees`);
      setEmployees(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      if (editingReview) {
        await axios.put(`${API_URL}/api/hr/reviews/${editingReview.id}`, formData);
        toast.success('Review updated');
      } else {
        await axios.post(`${API_URL}/api/hr/reviews`, formData);
        toast.success('Review created');
      }
      setShowDialog(false);
      setEditingReview(null);
      setFormData({});
      fetchReviews();
    } catch (err) {
      toast.error('Failed to save review');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this review?')) {
      try {
        await axios.delete(`${API_URL}/api/hr/reviews/${id}`);
        toast.success('Review deleted');
        fetchReviews();
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  const handleSubmit = async (id) => {
    try {
      await axios.put(`${API_URL}/api/hr/reviews/${id}`, { status: 'submitted' });
      toast.success('Review submitted');
      fetchReviews();
    } catch (err) {
      toast.error('Failed to submit');
    }
  };

  const openEdit = (review) => {
    setEditingReview(review);
    setFormData({ ...review });
    setShowDialog(true);
  };

  const openNew = () => {
    setEditingReview(null);
    setFormData({ overall_rating: 3, review_period: new Date().getFullYear().toString() });
    setShowDialog(true);
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? `${emp.first_name} ${emp.last_name}` : '-';
  };

  const filteredReviews = reviews.filter(r => 
    filterStatus === 'all' || r.status === filterStatus
  );

  const stats = {
    total: reviews.length,
    draft: reviews.filter(r => r.status === 'draft').length,
    avgRating: reviews.length > 0 ? (reviews.reduce((sum, r) => sum + (r.overall_rating || 0), 0) / reviews.length).toFixed(1) : 0,
    outstanding: reviews.filter(r => r.overall_rating === 5).length
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Performance Reviews</h1>
          <p className="text-muted-foreground">Evaluate and track employee performance.</p>
        </div>
        <Button onClick={openNew} className="gap-2"><Plus className="h-4 w-4" /> New Review</Button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><FileText className="h-8 w-8 text-muted-foreground" /><div><div className="text-2xl font-bold">{stats.total}</div><div className="text-sm text-muted-foreground">Total Reviews</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><Pencil className="h-8 w-8 text-amber-500" /><div><div className="text-2xl font-bold">{stats.draft}</div><div className="text-sm text-muted-foreground">Drafts</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><TrendingUp className="h-8 w-8 text-blue-500" /><div><div className="text-2xl font-bold">{stats.avgRating}</div><div className="text-sm text-muted-foreground">Avg Rating</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><Award className="h-8 w-8 text-emerald-500" /><div><div className="text-2xl font-bold">{stats.outstanding}</div><div className="text-sm text-muted-foreground">Outstanding</div></div></div></CardContent></Card>
      </div>

      <div className="flex items-center gap-4">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="acknowledged">Acknowledged</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Review Date</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredReviews.map((review) => (
              <TableRow key={review.id}>
                <TableCell className="font-medium">{getEmployeeName(review.employee_id)}</TableCell>
                <TableCell>{review.review_period}</TableCell>
                <TableCell className="text-muted-foreground">{format(new Date(review.review_date), 'MMM d, yyyy')}</TableCell>
                <TableCell><StarRating rating={review.overall_rating} readonly /></TableCell>
                <TableCell><Badge className={statusColors[review.status]}>{review.status}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(review)}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                      {review.status === 'draft' && (
                        <DropdownMenuItem onClick={() => handleSubmit(review.id)}><CheckCircle className="h-4 w-4 mr-2" /> Submit</DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(review.id)}>
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredReviews.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center">
                  <Star className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">No performance reviews found</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingReview ? 'Edit Review' : 'New Performance Review'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employee *</Label>
                <Select value={formData.employee_id || ''} onValueChange={v => setFormData({...formData, employee_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Review Period *</Label>
                <Input value={formData.review_period || ''} onChange={e => setFormData({...formData, review_period: e.target.value})} placeholder="Q4 2024" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Overall Rating *</Label>
              <StarRating rating={formData.overall_rating || 3} onChange={r => setFormData({...formData, overall_rating: r})} />
            </div>

            <div className="space-y-2">
              <Label>Goals Achieved</Label>
              <Textarea value={formData.goals_achieved || ''} onChange={e => setFormData({...formData, goals_achieved: e.target.value})} rows={2} placeholder="Key accomplishments..." />
            </div>

            <div className="space-y-2">
              <Label>Strengths</Label>
              <Textarea value={formData.strengths || ''} onChange={e => setFormData({...formData, strengths: e.target.value})} rows={2} placeholder="Key strengths demonstrated..." />
            </div>

            <div className="space-y-2">
              <Label>Areas for Improvement</Label>
              <Textarea value={formData.areas_for_improvement || ''} onChange={e => setFormData({...formData, areas_for_improvement: e.target.value})} rows={2} placeholder="Skills to develop..." />
            </div>

            <div className="space-y-2">
              <Label>Goals for Next Period</Label>
              <Textarea value={formData.goals_next_period || ''} onChange={e => setFormData({...formData, goals_next_period: e.target.value})} rows={2} placeholder="Objectives for next review period..." />
            </div>

            <div className="space-y-2">
              <Label>Additional Comments</Label>
              <Textarea value={formData.comments || ''} onChange={e => setFormData({...formData, comments: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>{editingReview ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PerformanceReviews;
